import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import './Toast.css';

export type ToastType = 'success' | 'error';

type ToastProps = {
    message: string;
    type: ToastType;
    onClose: () => void;
    duration?: number;
};

export function Toast({ message, type, onClose, duration = 3000 }: ToastProps) {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setVisible(false);
            setTimeout(onClose, 300);
        }, duration);
        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div className={`toast toast-${type} ${visible ? 'toast-enter' : 'toast-exit'}`}>
            <span className="toast-icon">
                {type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            </span>
            <span className="toast-message">{message}</span>
            <button type="button" className="toast-close" onClick={() => { setVisible(false); setTimeout(onClose, 300); }}>
                <X size={14} />
            </button>
        </div>
    );
}

/** Container to render toasts at the top of the screen */
export function ToastContainer({ toasts, removeToast }: {
    toasts: { id: number; message: string; type: ToastType }[];
    removeToast: (id: number) => void;
}) {
    if (toasts.length === 0) return null;
    return (
        <div
            className="toast-container"
            role="status"
            aria-live="polite"
            aria-atomic="false"
        >
            {toasts.map(t => (
                <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
            ))}
        </div>
    );
}

/** Hook to manage toasts */
let toastId = 0;
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
    const [toasts, setToasts] = useState<{ id: number; message: string; type: ToastType }[]>([]);

    const addToast = (message: string, type: ToastType = 'success') => {
        const id = ++toastId;
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return { toasts, addToast, removeToast };
}
